// lib/world/mood.js
//
// THE WORLD'S MOOD, AS SHADER STATE. One shared set of TSL uniforms (the
// same global-uniform pattern as effects/wind/Wind.jsx) that every
// mood-aware material reads, plus plain-JS mirrors for React code that
// drives lights/visibility. MoodRig.jsx smooths the live sim environment
// into these each frame — materials never touch the store.
//
//   nightness  0 day → 1 deep night          (from dayPhase)
//   wetness    0 dry → 1 downpour            (mood-derived, see below)
//   overcast   0 clear → 1 lidded sky        (mood-derived)
//   danger     raw signal (wind, unease)
//   vitality   raw signal (colour life)
//   warmth     raw signal (light temperature)
//
// WEATHER = MOOD. The sky doesn't have weather; the world's STATE does:
//   • danger brings wind and hard rain — the world braces
//   • low vitality + low warmth drizzles — the world going grey
//   • abundance clears the sky — plenty reads as bright weather
// So a governance shift or a rough patch for Victor is legible from the
// window before you read a single number.

import { uniform } from 'three/tsl'

export const mood = {
  // TSL uniforms — read these inside colorNode/positionNode functions.
  nightness: uniform(0),
  wetness: uniform(0),
  overcast: uniform(0),
  danger: uniform(0),
  vitality: uniform(0.55),
  warmth: uniform(1.0),
}

// Plain-JS mirrors (same values) for React-side drivers (lights, rain
// visibility, sky clouds). Updated by MoodRig alongside the uniforms.
export const moodState = {
  nightness: 0,
  wetness: 0,
  overcast: 0,
  danger: 0,
  vitality: 0.55,
  warmth: 1.0,
  abundance: 1.0,
  // Has the day stopped turning over? See sunPhase in sim world/Clock.js.
  sunStuck: false,
  // WHERE THE SUN IS. Equal to dayPhase whenever the clock is kept, and
  // held still when it is not. Everything that DRAWS the sun reads this;
  // everything that asks what time it is reads dayPhase.
  sunPhase: 0.5,
  // Where the world is in its day, 0 midnight to 1 midnight. Deliberately
  // NOT one of the smoothed keys: it wraps 1 -> 0 every world day, and
  // easing across that seam would walk the sun backwards through the
  // entire sky at midnight. It is written raw, and it moves slowly
  // enough on its own (a full circle per real hour) that nothing needs
  // smoothing anyway.
  dayPhase: 0.5,
}

/**
 * dayPhase (0=midnight … 0.25 dawn … 0.5 noon … 0.75 dusk) → nightness.
 * Matches the sky2 palette bands: full night below ~0.20 and above ~0.82,
 * daylight between 0.28 and 0.74, smooth ramps at dawn/dusk.
 */
export function nightnessFor(dayPhase) {
  const p = ((dayPhase % 1) + 1) % 1
  const smooth = (a, b, x) => {
    const t = Math.min(1, Math.max(0, (x - a) / (b - a)))
    return t * t * (3 - 2 * t)
  }
  // Rebalanced for a world people visit rather than survey. The old bands
  // spent 42% of the cycle at FULL night against 50% day, and with a 5-minute
  // day (Environment.js: simTime % 300) that's ~2 minutes of darkness every
  // five, so a short visit could be mostly night. Deep night is now ~18% and
  // the dawn/dusk ramps are much wider, which also buys a long golden hour:
  // the warmest light in the palette and by far the best to photograph him in.
  const morning = smooth(0.04, 0.26, p)   // 0→1 across a long dawn
  const evening = smooth(0.74, 0.96, p)   // 0→1 across a long dusk
  // midnight: 1-0+0=1 · noon: 1-1+0=0 · post-dusk: 1-1+1=1
  return Math.max(0, Math.min(1, 1 - morning + evening))
}

/**
 * Pure mapping: live environment → mood targets. Smoothing happens in
 * MoodRig; this is just the formula (kept pure so it's easy to reason
 * about and tune).
 */
export function computeMoodTargets(env) {
  const dayPhase = env?.dayPhase ?? 0.5
  // WHERE THE SUN IS, which is not always what time it is.
  //
  // The town keeps time by a clock Pino has to wind. Let it go long
  // enough and the day stops turning over: the sim holds sunPhase where
  // it was and keeps dayPhase running, so the hours carry on, places
  // still open and shut, he still gets tired, and all of it happens under
  // a sun that has not moved since yesterday.
  //
  // Falls back to dayPhase, so an older sim that does not send this, or a
  // frame that arrives before the first environment does, draws an
  // ordinary sky rather than a stuck one.
  const skyPhase = env?.sunPhase ?? dayPhase
  const danger = Math.max(0, Math.min(1, env?.danger ?? 0))
  const vitality = Math.max(0, Math.min(1, env?.vitality ?? 0.55))
  // warmth/abundance run 0..2 in the sim (1 = baseline) — normalise to 0..1.
  const warmth = Math.max(0, Math.min(1, (env?.warmth ?? 1.0) / 2))
  const abundance = Math.max(0, Math.min(1, (env?.abundance ?? 1.0) / 2))

  // Weather is DERIVED from the world's state: the sim broadcasts danger,
  // vitality, warmth and abundance and never sends weather itself, so the
  // client works it out and both ends agree without another field.
  //
  // An explicit value wins when one is given, which is how a tool can ask
  // for rain on a calm day. Nothing in production sends these (see
  // serializeEnvironment), so the derived path is still the only one the
  // live world takes; the director sets them directly, and before this its
  // rain and overcast sliders moved a number that nothing ever read.
  const derivedWet = Math.max(0, Math.min(1,
    danger * 0.75 +
    Math.max(0, 0.5 - vitality) * 0.9 +
    Math.max(0, 0.45 - warmth) * 0.6 -
    Math.max(0, abundance - 0.5) * 0.5,
  ))
  const wetness = typeof env?.wetness === 'number'
    ? Math.max(0, Math.min(1, env.wetness))
    : derivedWet
  const overcast = typeof env?.overcast === 'number'
    ? Math.max(0, Math.min(1, env.overcast))
    : Math.max(0, Math.min(1, wetness * 0.85 + danger * 0.3))

  return {
    nightness: nightnessFor(skyPhase),
    wetness,
    overcast,
    danger,
    vitality,
    warmth,
    abundance,
    // Not used by the shaders; carried so the HUD and anything else that
    // wants to say the day has stopped can, without asking the sim twice.
    sunStuck: !!env?.sunStuck,
  }
}
